import Typography from '@mui/material/Typography';
import FormField from 'further-ui/components/FormField';
import { Row } from 'further-ui/components/Layout';
import api from 'lib/trpcClient';
import { kycProcessStatusLabels, kycRiskRatingLabels } from 'further-ui/labels';
import {
  canUnapproveAml,
  canApproveIdv,
  canApproveAml,
} from 'further-ui/utils';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { getQueryKey } from '@trpc/react-query';
import ManualKycDetailsForm from './ManualKycDetailsForm';
import AmlResult from './AmlResult';
import { Disclosure } from 'further-ui/hooks';
import downloadKycPdf from 'helpers/investor/downloadKycPdf';
import { KycRiskRating } from 'further-types/kyc';
import ReviewButton from '../ReviewButton';

const kycRiskRatingLabelsMap = Object.fromEntries(
  kycRiskRatingLabels.map((label) => [label.value, label.label]),
);

type Props = {
  vehicleId: string;
  vehicleApplicationId: string;
  investorId: string;
  isStepCompleted: boolean;
  manualKycDisclosure: Disclosure;
  fullName: string;
};

const KycDetails: React.FC<Props> = ({
  vehicleId,
  vehicleApplicationId,
  investorId,
  isStepCompleted,
  manualKycDisclosure,
  fullName,
}) => {
  const apiUtils = api.useUtils();
  const [kycDetails] = api.lpKyc.getKycDetails.useSuspenseQuery({
    vehicleId,
    vehicleApplicationId,
  });

  const [investorKyc] = api.lpKyc.getInvestorKyc.useSuspenseQuery({
    vehicleId,
    investorId,
  });

  const invalidations = [
    getQueryKey(api.lpKyc.getKycDetails, {
      vehicleId,
      vehicleApplicationId,
    }),
    getQueryKey(api.lpKyc.getInvestorKyc, { vehicleId, investorId }),
  ];

  const approveIdv = api.lpKyc.approveIdv.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'IDV approved successfully.',
      invalidations,
    }),
  );

  const unapproveAml = api.lpKyc.unapproveAml.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'AML unapproved successfully.',
      invalidations,
    }),
  );

  const approveAml = api.lpKyc.approveAml.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'AML approved successfully.',
      invalidations,
    }),
  );

  const archiveAmlHit = api.lpKyc.archiveAmlHit.useMutation(
    useHandleTrpcMutation()({ invalidations }),
  );
  const unarchiveAmlHit = api.lpKyc.unarchiveAmlHit.useMutation(
    useHandleTrpcMutation()({ invalidations }),
  );

  const isPending =
    unapproveAml.isPending ||
    approveAml.isPending ||
    approveIdv.isPending ||
    archiveAmlHit.isPending ||
    unarchiveAmlHit.isPending;

  const files = kycDetails?.answers.flatMap((answer) => answer.answerFiles);
  const manualApprovalFiles = investorKyc?.manualApprovalFiles;
  const showDownloadResultsButton = !!(
    investorKyc?.hasYotiData && investorKyc.isKycStepCompleted
  );

  const showUnapproveAmlButton =
    canUnapproveAml(investorKyc) && !isStepCompleted;
  const showApproveIdvButton = canApproveIdv(investorKyc);
  const showApproveAmlButton = canApproveAml(investorKyc);

  const handleApproveIdv = () => {
    if (isPending) return;
    approveIdv.mutate({ vehicleId, investorId });
  };
  const handleUnapproveAml = () => {
    if (isPending) return;
    unapproveAml.mutate({ vehicleId, investorId });
  };
  const handleApproveAml = () => {
    if (isPending) return;
    approveAml.mutate({ vehicleId, investorId });
  };
  const handleArchiveAmlHit = (caDocId: string) => {
    if (isPending) return;
    archiveAmlHit.mutate({ vehicleId, investorId, caDocId });
  };
  const handleUnarchiveAmlHit = (caDocId: string) => {
    if (isPending) return;
    unarchiveAmlHit.mutate({ vehicleId, investorId, caDocId });
  };

  const handleOpenManualKycDetailsForm = () => {
    manualKycDisclosure.onOpen();
  };

  const handleDownloadResults = async () => {
    if (!investorKyc) {
      return;
    }

    const images = await apiUtils.lpKyc.getImages.fetch({
      investorId,
      vehicleId,
    });
    void downloadKycPdf(investorKyc, images, { fullName });
  };

  return (
    <>
      <FormField label="IDV status" spacing="xxs">
        <Row spacing="xs">
          <Typography variant="body2" color="grey.600">
            {investorKyc?.idvProcessStatus
              ? kycProcessStatusLabels[investorKyc.idvProcessStatus]
              : '--'}
          </Typography>
          {showApproveIdvButton ? (
            <ReviewButton
              variant="text"
              color="primary"
              onClick={handleApproveIdv}
              disabled={isPending}
            >
              Approve
            </ReviewButton>
          ) : null}
          {showDownloadResultsButton ? (
            <ReviewButton
              variant="text"
              color="primary"
              onClick={handleDownloadResults}
            >
              Download results
            </ReviewButton>
          ) : null}
        </Row>
      </FormField>
      <FormField label="AML status" spacing="xxs">
        <Row spacing="xs">
          <Typography variant="body2" color="grey.600">
            {investorKyc?.amlProcessStatus
              ? kycProcessStatusLabels[investorKyc.amlProcessStatus]
              : '--'}
          </Typography>
          {showUnapproveAmlButton ? (
            <ReviewButton
              variant="text"
              color="primary"
              onClick={handleUnapproveAml}
              disabled={isPending}
            >
              Unapprove
            </ReviewButton>
          ) : null}
          {showApproveAmlButton ? (
            <ReviewButton
              variant="text"
              color="primary"
              onClick={handleApproveAml}
              disabled={isPending}
            >
              Approve
            </ReviewButton>
          ) : null}
        </Row>
      </FormField>
      <AmlResult
        vehicleId={vehicleId}
        investorId={investorId}
        investorKyc={investorKyc}
        archiveAmlHit={handleArchiveAmlHit}
        unarchiveAmlHit={handleUnarchiveAmlHit}
        isPending={isPending}
      />
      <FormField label="Risk rating" spacing="xs">
        <Row spacing="xs" centered>
          <Typography variant="body2" color="grey.600">
            {investorKyc?.riskRating
              ? kycRiskRatingLabelsMap[investorKyc.riskRating]
              : kycRiskRatingLabelsMap[KycRiskRating.Low]}
          </Typography>
          <ReviewButton
            variant="text"
            color="primary"
            onClick={handleOpenManualKycDetailsForm}
          >
            Edit
          </ReviewButton>
        </Row>
      </FormField>
      <FormField label="Custom questions responses" spacing="xxs">
        <Typography variant="body2" color="grey.600">
          {kycDetails?.answers.length ? 'Completed' : '--'}
        </Typography>
      </FormField>
      <FormField label="Custom question files" spacing="xxs">
        {files?.map((file) => (
          <Row spacing="xs" key={file.path}>
            <Typography variant="body2" color="grey.600">
              {file.name}
            </Typography>
            <ReviewButton variant="text" color="primary" href={file.previewUrl}>
              Download
            </ReviewButton>
          </Row>
        )) ?? (
          <Typography variant="body2" color="grey.600">
            --
          </Typography>
        )}
      </FormField>

      {manualApprovalFiles?.length ? (
        <FormField label="Admin-uploaded files" spacing="xxs">
          {manualApprovalFiles.map((file) => (
            <Row spacing="xs" key={file.path}>
              <Typography variant="body2" color="grey.600">
                {file.name}
              </Typography>
              <ReviewButton
                variant="text"
                color="primary"
                href={file.previewUrl}
              >
                Download
              </ReviewButton>
            </Row>
          ))}
        </FormField>
      ) : null}

      <FormField label="Notes" spacing="xxs">
        <Typography variant="body2" color="grey.600">
          {investorKyc?.manualApprovalNotes ?? '--'}
        </Typography>
        <ReviewButton
          variant="text"
          color="primary"
          onClick={handleOpenManualKycDetailsForm}
        >
          Edit notes
        </ReviewButton>
      </FormField>

      {manualKycDisclosure.isOpen ? (
        <ManualKycDetailsForm
          title="Manual KYC record"
          investorKyc={investorKyc ?? undefined}
          vehicleId={vehicleId}
          investorId={investorId}
          onClose={manualKycDisclosure.onClose}
        />
      ) : null}
    </>
  );
};

export default KycDetails;
